import { FormControl, FormControlProps, FormErrorMessage, FormLabel } from "@chakra-ui/react";
import { useMemo } from "react";
import { ControllerProps, FieldPath, FieldValues, get, useFormContext, useFormState } from "react-hook-form";

export interface ControlledFormControlProperties<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Pick<ControllerProps<TFieldValues, TName>, "control" | "name">,
    Omit<FormControlProps, "children" | "label"> {
  children?: React.ReactNode;

  isCentered?: boolean;

  label?: React.ReactNode;
}

export const ControlledFormControl = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  children,
  control,
  isCentered,
  label,
  name,
  size,
  textAlign,
  ...rest
}: ControlledFormControlProperties<TFieldValues, TName>): React.ReactElement => {
  const methods = useFormContext<TFieldValues>();
  const formState = useFormState({
    control: control ?? methods?.control,
    name,
  });

  const fieldState = useMemo(() => {
    const error = get(formState.errors, name);
    return {
      error: (error as Error | undefined)?.message,
      invalid: !!error,
    };
  }, [formState, name]);

  return (
    <FormControl
      alignItems={isCentered ? "center" : "stretch"}
      display="flex"
      flexDirection="column"
      isInvalid={fieldState.invalid}
      {...rest}
    >
      {label != null && (
        <FormLabel
          data-testid="ControlledInputFormLabel"
          fontSize={size}
          fontWeight={size === "sm" ? "normal" : undefined}
          mb={size === "sm" ? 0.5 : undefined}
          textAlign={isCentered ? "center" : textAlign}
        >
          {label}
        </FormLabel>
      )}
      {children}
      {fieldState.error && <FormErrorMessage>{fieldState.error}</FormErrorMessage>}
    </FormControl>
  );
};
